/**
 * Invoice API Wrapper
 *
 * Provides methods for endpoints not yet included in the generated OpenAPI client.
 * Once `pnpm run api:generate` includes these endpoints, migrate callers to use
 * `invoiceApi` directly and remove this file.
 *
 * TO REMOVE THIS FILE:
 * 1. Run: pnpm run api:generate
 * 2. Verify the following methods exist in InvoiceApi:
 *    - apiVversionInvoiceIdPayPost
 *    - apiVversionInvoiceIdMatchingLedgerEntriesGet
 *    - apiVversionInvoiceIdLinkLedgerEntryPost
 * 3. Remove this file and update callers to use invoiceApi directly.
 */

import { InvoiceApi } from 'axiosApi/apis/invoice-api';
import { setApiKeyToObject } from 'axiosApi/common';
import { AxiosResponse } from 'axios';

export interface PayInvoiceRequest {
  amount: number;      // integer, already × 100
  incoming: number;    // 0 = outgoing (debit), 1 = incoming (credit)
  idAccount: number;
  description: string;
  date: Date;
}

export interface MatchingLedgerEntry {
  id: number;
  amount: number;       // integer × 100
  incoming: number;
  date: string;         // ISO date string
  description: string;
  account?: { id: number; name: string; accountNumber: string };
}

export class InvoiceApiWrapper {
  private invoiceApi: InvoiceApi;

  constructor(invoiceApi: InvoiceApi) {
    this.invoiceApi = invoiceApi;
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const configuration = (this.invoiceApi as any).configuration;
    const headers: Record<string, string> = {};
    await setApiKeyToObject(headers, 'Userentity', configuration);
    await setApiKeyToObject(headers, 'Referer', configuration);
    await setApiKeyToObject(headers, 'Authorization', configuration);
    return headers;
  }

  /**
   * Atomically marks an invoice as Paid:
   * creates an AccountingSeat, links it to the invoice, and
   * sets the workflow state to Paid (118) — all in one transaction.
   *
   * POST /api/v{version}/Invoice/{id}/pay
   */
  async payInvoice(
    version: string,
    id: number,
    body: PayInvoiceRequest,
    options?: any
  ): Promise<AxiosResponse<any>> {
    // Check if the method exists in the generated client (after api:generate)
    if ('apiVversionInvoiceIdPayPost' in this.invoiceApi) {
      return (this.invoiceApi as any).apiVversionInvoiceIdPayPost(version, id, body, options);
    }

    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/${id}/pay`,
      JSON.stringify(body),
      { headers, ...options }
    );
  }

  /**
   * Returns existing ledger entries (AccountingSeat) whose amount matches
   * the given value and whose date falls within the same month up to that date.
   *
   * GET /api/v{version}/Invoice/{id}/matching-ledger-entries?amount={amount}&date={date}
   */
  async getMatchingLedgerEntries(
    version: string,
    invoiceId: number,
    amount: number,   // integer × 100
    date: Date,
    options?: any
  ): Promise<AxiosResponse<{ data: MatchingLedgerEntry[] }>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    const dateStr = date.toISOString().split('T')[0];

    return axiosInstance.get(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/matching-ledger-entries`,
      { params: { amount, date: dateStr }, headers, ...options }
    );
  }

  /**
   * Links an already-existing AccountingSeat to the invoice and transitions it
   * to the Paid state — without creating a new accounting seat row.
   *
   * POST /api/v{version}/Invoice/{id}/link-ledger-entry
   */
  async linkExistingLedgerEntry(
    version: string,
    invoiceId: number,
    accountingSeatId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/link-ledger-entry`,
      JSON.stringify({ accountingSeatId }),
      { headers, ...options }
    );
  }

  /**
   * Marks an invoice as Paid without creating any ledger entry.
   * Reconciliation with an AccountingSeat can be done later from the accounts module.
   *
   * POST /api/v{version}/Invoice/{id}/mark-paid
   */
  async markInvoiceAsPaid(
    version: string,
    invoiceId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/mark-paid`,
      null,
      { headers, ...options }
    );
  }

  /**
   * Links two existing ledger entries across different accounts.
   * The source and target must have opposite directions (one debit, one credit).
   *
   * POST /api/v{version}/Ledger/{id}/link
   */
  async linkLedgerEntries(
    version: string,
    sourceSeatId: number,
    targetSeatId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Ledger/${sourceSeatId}/link`,
      JSON.stringify({ targetSeatId }),
      { headers, ...options }
    );
  }

  /**
   * Removes the "linked" association from a ledger entry and its counterpart.
   *
   * DELETE /api/v{version}/Ledger/{id}/link
   */
  async unlinkLedgerEntries(
    version: string,
    seatId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.delete(
      `${basePath}/api/v${version}/Ledger/${seatId}/link`,
      { headers, ...options }
    );
  }

  /**
   * Returns ledger entries that are candidates to be linked to the given entry:
   * opposite direction, no existing link, same corporate entity, different account.
   *
   * GET /api/v{version}/Ledger/{id}/link-candidates?term=
   */
  async getLedgerLinkCandidates(
    version: string,
    seatId: number,
    term = '',
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.get(
      `${basePath}/api/v${version}/Ledger/${seatId}/link-candidates`,
      { params: { term }, headers, ...options }
    );
  }

  /**
   * Updates the workflow state of a DynamoDB invoice (module 5, identified by jobId).
   * Used when no MySQL entity exists yet.
   *
   * POST /api/v{version}/Invoice/job/{jobId}/workflow-state
   */
  async updateJobInvoiceWorkflowState(
    version: string,
    jobId: string,
    workflowStateId: number,
    comments?: string,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/job/${jobId}/workflow-state`,
      JSON.stringify({ workflowStateId, comments }),
      { headers, ...options }
    );
  }

  /**
   * Returns the invoices currently linked to a ledger entry for reconciliation.
   *
   * GET /api/v{version}/Ledger/{id}/reconciliation
   */
  async getSeatReconciliation(
    version: string,
    seatId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.get(
      `${basePath}/api/v${version}/Ledger/${seatId}/reconciliation`,
      { headers, ...options }
    );
  }

  /**
   * Links one or more invoices to a ledger entry for reconciliation.
   *
   * POST /api/v{version}/Ledger/{id}/reconcile
   */
  async reconcileSeat(
    version: string,
    seatId: number,
    invoiceAllocations: Record<number, number | null>,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Ledger/${seatId}/reconcile`,
      JSON.stringify({ invoiceAllocations }),
      { headers, ...options }
    );
  }

  /**
   * Removes the reconciliation link between a ledger entry and an invoice.
   *
   * DELETE /api/v{version}/Ledger/{id}/reconcile/{invoiceId}
   */
  async unreconcileSeat(
    version: string,
    seatId: number,
    invoiceId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.delete(
      `${basePath}/api/v${version}/Ledger/${seatId}/reconcile/${invoiceId}`,
      { headers, ...options }
    );
  }

  /**
   * Returns the credit ledger entries currently linked to an invoice for reconciliation.
   *
   * GET /api/v{version}/Invoice/{id}/reconciliation
   */
  async getInvoiceLinkedSeats(
    version: string,
    invoiceId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.get(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/reconciliation`,
      { headers, ...options }
    );
  }

  /**
   * Returns available credit ledger entries that can be linked to this invoice.
   *
   * GET /api/v{version}/Invoice/{id}/reconcile-candidates?term=&page=&pageSize=
   */
  async searchInvoiceReconcileCandidates(
    version: string,
    invoiceId: number,
    term = '',
    page = 1,
    pageSize = 20,
    incoming = 1,
    idAccount?: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    const params: Record<string, any> = { term, page, pageSize, incoming };
    if (idAccount != null) params.idAccount = idAccount;

    return axiosInstance.get(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/reconcile-candidates`,
      { params, headers, ...options }
    );
  }

  /**
   * Links one or more credit ledger entries to an invoice for reconciliation.
   *
   * POST /api/v{version}/Invoice/{id}/reconcile
   */
  async reconcileInvoice(
    version: string,
    invoiceId: number,
    seatAllocations: Record<number, number | null>,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    headers['Content-Type'] = 'application/json-patch+json';

    return axiosInstance.post(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/reconcile`,
      JSON.stringify({ seatAllocations }),
      { headers, ...options }
    );
  }

  /**
   * Removes the reconciliation link between a credit ledger entry and this invoice.
   *
   * DELETE /api/v{version}/Invoice/{id}/reconcile/{seatId}
   */
  async unreconcileInvoiceFromSeat(
    version: string,
    invoiceId: number,
    seatId: number,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.delete(
      `${basePath}/api/v${version}/Invoice/${invoiceId}/reconcile/${seatId}`,
      { headers, ...options }
    );
  }

  /**
   * Uploads an HTML invoice template to S3 for the given corporate entity and country.
   * Returns the S3 key (e.g. "invoice-templates/5-AR.html") to be stored in
   * AdditionalSettings.templateS3Key of the invoice configuration.
   *
   * POST /api/v{version}/InvoiceConfiguration/template  (multipart/form-data)
   */
  async uploadInvoiceTemplate(
    version: string,
    corporateEntityId: number,
    countryCode: string,
    templateFile: File,
    options?: any
  ): Promise<AxiosResponse<{ data: string }>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();
    // Do NOT set Content-Type manually — axios sets multipart/form-data with boundary automatically.

    const formData = new FormData();
    formData.append('templateFile', templateFile);
    formData.append('corporateEntityId', String(corporateEntityId));
    formData.append('countryCode', countryCode);

    return axiosInstance.post(
      `${basePath}/api/v${version}/InvoiceConfiguration/template`,
      formData,
      { headers, ...options }
    );
  }

  /**
   * Returns Paid (AR) invoices not yet fully reconciled, scoped to the corporate entity.
   * Each result includes allocatedAmount and remainingAmount.
   *
   * GET /api/v{version}/Invoice/paid-unreconciled/search
   */
  async searchPaidUnreconciled(
    version: string,
    term = '',
    pageNum = 1,
    pageSize = 50,
    seatCurrencyId = 0,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.get(
      `${basePath}/api/v${version}/Invoice/paid-unreconciled/search`,
      { params: { term, pageNum, pageSize, seatCurrencyId }, headers, ...options }
    );
  }

  /**
   * Returns AP invoices (received by the corporate entity) not yet fully reconciled.
   * Each result includes allocatedAmount and remainingAmount.
   * No workflow-state filter — AP invoices use their own workflow.
   *
   * GET /api/v{version}/Invoice/ap-unreconciled/search
   */
  async searchApUnreconciled(
    version: string,
    term = '',
    pageNum = 1,
    pageSize = 50,
    seatCurrencyId = 0,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.get(
      `${basePath}/api/v${version}/Invoice/ap-unreconciled/search`,
      { params: { term, pageNum, pageSize, seatCurrencyId }, headers, ...options }
    );
  }

  /**
   * Hard-deletes an AP invoice from DynamoDB (the AP processing queue).
   * Requires accountspayable.edit scope on the backend.
   *
   * DELETE /api/v{version}/Invoice/accountPayable/{jobId}
   */
  async deleteApInvoice(
    version: string,
    jobId: string,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.delete(
      `${basePath}/api/v${version}/Invoice/accountPayable/${encodeURIComponent(jobId)}`,
      { headers, ...options }
    );
  }

  /**
   * Returns a paginated list of invoices filtered by entity.
   * When issuedByEntity=true, returns invoices where PersonaFrom belongs to the entity (employee/provider).
   * When issuedByEntity=false, returns invoices where PersonaTo belongs to the entity (client).
   *
   * GET /api/v{version}/Invoice/searchByEntity
   */
  async searchInvoicesByEntity(
    version: string,
    entityId: number,
    entityTypeId: number,
    issuedByEntity: boolean,
    term = '',
    pageNum = 1,
    pageSize = 20,
    options?: any
  ): Promise<AxiosResponse<any>> {
    const axiosInstance = (this.invoiceApi as any).axios;
    const basePath: string = (this.invoiceApi as any).basePath;
    const headers = await this.buildHeaders();

    return axiosInstance.get(
      `${basePath}/api/v${version}/Invoice/searchByEntity`,
      { params: { entityId, entityTypeId, issuedByEntity, term, pageNum, pageSize }, headers, ...options }
    );
  }
}
